import { DestroyRef, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { AppState } from '../../../../reducers';
import { StartFlowGlobalStore } from '../../../../start-flow/store/start-flow-global.store';
import { AccountGlobalStore } from '../../../../account/store/account-global.store';
import { AccountService } from '../../../../account/services/account.service';
import { SearchDrawersNetworksStore } from '../../../../sidedrawer-networks/store/search-drawers-networks.store';
import { CoreRoutes } from '../../../../core/routes/core.routes';
import { MobileAccountRoutesEnum } from '../../account/routes/mobile-account-routes.enum';
import { BiometricsService } from '../../biometrics/services/biometrics.service';
import { PreferencesService } from '../../biometrics/services/preferences.service';
import { BiometricsActions } from '../../biometrics/store/biometrics.store';

@Injectable()
export class MobileStartFlowStore {
  private readonly startFlowStore = inject(StartFlowGlobalStore);
  private readonly ngrxStore = inject<Store<AppState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly accountGlobalStore = inject(AccountGlobalStore);
  private readonly accountService = inject(AccountService);
  private readonly searchDrawersNetworksStore = inject(
    SearchDrawersNetworksStore
  );
  private readonly biometricsService = inject(BiometricsService);
  private readonly preferencesService = inject(PreferencesService);

  private biometricsRedirectPending = false;

  initialize(): void {
    this.startFlowStore.setSecurityCompleteOverride(() =>
      this.onSecurityComplete()
    );

    this.actions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(BiometricsActions.completedBiometricsOnRegister)
      )
      .subscribe(() => {
        if (this.biometricsRedirectPending) {
          this.biometricsRedirectPending = false;
          this.continueAfterBiometrics();
        }
      });
  }

  private async onSecurityComplete(): Promise<void> {
    const [{ isAvailable }, { value: hasLoggedIn }] = await Promise.all([
      firstValueFrom(this.biometricsService.checkBiometrics()),
      firstValueFrom(this.preferencesService.haveAlreadyLoggedInDevice()),
    ]);

    this.preferencesService
      .setFirstDeviceLogin()
      .pipe(
        take(1),
        catchError(error => {
          console.warn('Failed to set first device login:', error);
          return of(null);
        })
      )
      .subscribe();

    if (isAvailable && !hasLoggedIn) {
      this.biometricsRedirectPending = true;
      // NOTE: Signal start of first-time biometrics setup so
      // MobileAuthorizeComponent and MobileAuthService cancel their safety
      // timers; otherwise the post-login watchdog forces a redirect to home
      // before the user finishes the activation screen, racing this
      // navigation and leaving the start-flow pipeline stuck waiting for
      // completedBiometricsOnRegister forever.
      this.ngrxStore.dispatch(
        BiometricsActions.firstTimeSetupNavigationStarted()
      );
      this.router.navigateByUrl(
        `/${CoreRoutes.root}/${MobileAccountRoutesEnum.root}/${MobileAccountRoutesEnum.activateBiometricsFirstTime}`
      );
      return;
    }

    this.ngrxStore.dispatch(BiometricsActions.completedBiometricsOnRegister());
    await this.continueAfterBiometrics();
  }

  async continueAfterBiometrics(): Promise<void> {
    const account = this.accountGlobalStore.account();
    const invitationCode = this.startFlowStore.invitationCode();

    if (invitationCode) {
      try {
        await firstValueFrom(
          this.accountService
            .sendInvitationCode(account, invitationCode)
            .pipe(catchError(() => of(null)))
        );
      } catch {
        // Continue even if invitation code fails
      }
      this.startFlowStore.invitationUsed();
    }

    const result = await this.searchDrawersNetworksStore.loadInitialNetworks();
    this.startFlowStore.onNetworksLoaded(result);
  }
}
